import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { trackOutboundLink, trackEvent } from '../../utils/analytics';
import { 
  IoLogoTiktok,
  IoLogoSnapchat, 
  IoLogoTwitter, 
  IoLogoYoutube, 
  IoLogoInstagram, 
  IoLogoFacebook, 
  IoLogoLinkedin,
  IoLogoWhatsapp,
  IoChatbubbleEllipsesOutline
} from 'react-icons/io5';
import { XMarkIcon } from '@heroicons/react/24/outline';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { language, changeLanguage, direction } = useLanguage();
  const isRTL = direction === 'rtl';
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<{text: string, sender: 'user' | 'bot', timestamp: Date}[]>([]);
  const [inputValue, setInputValue] = useState('');
  
  // Helper function to render icons safely
  const renderIcon = (Icon: any, size: number) => {
    return React.createElement(Icon, { size, style: { color: 'white' } });
  };
  
  // Chatbot responses data
  const botResponses = {
    greetings: {
      ar: [
        'وعليكم السلام ورحمة الله وبركاته، كيف يمكنني مساعدتك اليوم؟',
        'أهلاً ومرحباً بك في اضواء الحجاز للعمرة، كيف يمكنني خدمتك؟',
        'مرحباً بك! يسعدنا تواصلك معنا. كيف يمكنني مساعدتك؟'
      ],
      en: [
        'Hello! How can I help you today?',
        'Welcome to Adwaa Al-Hijaz Umrah! How may I assist you?',
        'Greetings! How can I be of service to you today?'
      ]
    },
    booking: {
      ar: [
        'يمكنك التواصل معنا على الواتساب للحجز والاستفسار عن الأسعار. سنكون سعداء بمساعدتك في اختيار الباقة المناسبة لك.',
        'أسعارنا تنافسية وتختلف حسب الموسم والفندق المختار. يمكنك التواصل معنا على الواتساب للحصول على عرض سعر مخصص.',
        'نقدم باقات متنوعة تناسب جميع الميزانيات. تواصل معنا على الواتساب وسنقدم لك أفضل الخيارات.'
      ],
      en: [
        'You can contact us on WhatsApp for booking and price inquiries. We would be happy to help you choose the right package for you.',
        'Our prices are competitive and vary by season and chosen hotel. Contact us on WhatsApp for a personalized quote.',
        'We offer various packages to suit all budgets. Contact us on WhatsApp and we will provide you with the best options.'
      ]
    },
    hotels: {
      ar: [
        'لدينا مجموعة متميزة من الفنادق في مكة والمدينة. تتراوح من 3 إلى 5 نجوم، وجميعها قريبة من الحرم.',
        'نتعاون مع أفضل الفنادق في مكة والمدينة لنقدم لك تجربة إقامة مريحة وممتعة. يمكنك الاطلاع على قائمة الفنادق في صفحة الفنادق.',
        'فنادقنا مختارة بعناية لتكون قريبة من الحرم وتوفر جميع وسائل الراحة. اطلع على التفاصيل في صفحة الفنادق.'
      ],
      en: [
        'We have a distinguished collection of hotels in Makkah and Madinah. They range from 3 to 5 stars, all close to the Holy Mosque.',
        'We partner with the best hotels in Makkah and Madinah to provide you with a comfortable and enjoyable stay. You can view the list of hotels on our hotels page.',
        'Our hotels are carefully selected to be close to the Holy Mosque and provide all amenities. Check the details on our hotels page.'
      ]
    },
    transportation: {
      ar: [
        'نوفر وسائل نقل حديثة ومريحة للتنقل بين المطار والفندق وبين مكة والمدينة.',
        'أسطول حافلاتنا مكيف بالكامل ومجهز بأحدث وسائل الراحة لضمان رحلة مريحة.',
        'نهتم براحتك أثناء التنقل، لذلك نوفر حافلات حديثة ومريحة مع سائقين محترفين.'
      ],
      en: [
        'We provide modern and comfortable transportation between the airport and hotel, and between Makkah and Madinah.',
        'Our bus fleet is fully air-conditioned and equipped with the latest amenities to ensure a comfortable journey.',
        'We care about your comfort during transportation, so we provide modern and comfortable buses with professional drivers.'
      ]
    },
    umrah: {
      ar: [
        'نقدم خدمات متكاملة للعمرة تشمل الإقامة والنقل والإرشاد الديني.',
        'فريقنا متخصص في تقديم خدمات العمرة بأعلى مستويات الجودة لضمان رحلة روحانية مميزة.',
        'نسعى لتوفير تجربة عمرة لا تُنسى من خلال الاهتمام بكافة التفاصيل من لحظة وصولك وحتى مغادرتك.'
      ],
      en: [
        'We provide comprehensive Umrah services including accommodation, transportation, and religious guidance.',
        'Our team specializes in providing Umrah services of the highest quality to ensure a distinctive spiritual journey.',
        'We strive to provide an unforgettable Umrah experience by taking care of all details from the moment you arrive until your departure.'
      ]
    },
    thanks: {
      ar: [
        'شكراً لتواصلك معنا. نحن سعداء بخدمتك!',
        'نشكرك على اهتمامك بخدماتنا. نتطلع للترحيب بك قريباً!',
        'شكراً لك! يسعدنا دائماً الإجابة على استفساراتك.'
      ],
      en: [
        'Thank you for contacting us. We are happy to serve you!',
        'Thank you for your interest in our services. We look forward to welcoming you soon!',
        'Thank you! We are always happy to answer your inquiries.'
      ]
    },
    default: {
      ar: [
        'شكراً لتواصلك معنا. كيف يمكنني مساعدتك؟',
        'يسعدني الرد على استفساراتك. هل هناك شيء محدد ترغب في معرفته؟',
        'أنا هنا للإجابة على أسئلتك. كيف يمكنني مساعدتك اليوم؟'
      ],
      en: [
        'Thank you for contacting us. How can I help you?',
        'I am happy to answer your inquiries. Is there something specific you would like to know?',
        'I am here to answer your questions. How can I assist you today?'
      ]
    }
  };
  
  // Get random response from category
  const getRandomResponse = (category: keyof typeof botResponses) => {
    const responses = botResponses[category][isRTL ? 'ar' : 'en'];
    const randomIndex = Math.floor(Math.random() * responses.length);
    return responses[randomIndex];
  };
  
  const handleSendMessage = () => {
    if (inputValue.trim() === '') return;
    
    const newUserMessage = {
      text: inputValue,
      sender: 'user' as const,
      timestamp: new Date()
    };
    
    setMessages([...messages, newUserMessage]);
    setInputValue('');
    
    // Process the message and respond
    setTimeout(() => {
      let botResponse = '';
      let isHotelQuery = false;
      let isBookingQuery = false;
      let isTransportQuery = false;
      
      const lowerInput = inputValue.toLowerCase();
      
      // Check for greetings
      if (lowerInput.includes('سلام') || 
          lowerInput.includes('مرحبا') ||
          lowerInput.includes('اهلا') ||
          lowerInput.includes('hello') || 
          lowerInput.includes('hi') ||
          lowerInput.includes('greet')) {
        botResponse = getRandomResponse('greetings');
      }
      // Check for booking inquiries
      else if (lowerInput.includes('حجز') || 
               lowerInput.includes('سعر') ||
               lowerInput.includes('تكلفة') ||
               lowerInput.includes('كام') ||
               lowerInput.includes('ليلة') ||
               lowerInput.includes('book') ||
               lowerInput.includes('price') ||
               lowerInput.includes('cost') ||
               lowerInput.includes('night')) {
        botResponse = getRandomResponse('booking');
        isBookingQuery = true;
      }
      // Check for hotel inquiries
      else if (lowerInput.includes('فندق') || 
               lowerInput.includes('اقامة') ||
               lowerInput.includes('سكن') ||
               lowerInput.includes('hotel') ||
               lowerInput.includes('stay') ||
               lowerInput.includes('accommodation')) {
        botResponse = getRandomResponse('hotels');
        isHotelQuery = true;
      }
      // Check for transportation inquiries
      else if (lowerInput.includes('نقل') || 
               lowerInput.includes('باص') ||
               lowerInput.includes('حافلة') ||
               lowerInput.includes('سيارة') ||
               lowerInput.includes('transport') ||
               lowerInput.includes('bus') ||
               lowerInput.includes('car')) {
        botResponse = getRandomResponse('transportation');
        isTransportQuery = true;
      }
      // Check for Umrah inquiries
      else if (lowerInput.includes('عمرة') || 
               lowerInput.includes('مناسك') ||
               lowerInput.includes('حج') ||
               lowerInput.includes('umrah') ||
               lowerInput.includes('hajj') ||
               lowerInput.includes('pilgrimage')) {
        botResponse = getRandomResponse('umrah');
      }
      // Check for thanks
      else if (lowerInput.includes('شكر') || 
               lowerInput.includes('thank')) {
        botResponse = getRandomResponse('thanks');
      }
      // Default response
      else {
        botResponse = getRandomResponse('default');
      }
      
      const newBotMessage = {
        text: botResponse,
        sender: 'bot' as const,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, newBotMessage]);
      
      // Add action buttons after response
      if (isBookingQuery) {
        setTimeout(() => {
          const actionMessage = {
            text: isRTL ? '<whatsapp-button>' : '<whatsapp-button>',
            sender: 'bot' as const,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, actionMessage]);
        }, 500);
      } else if (isHotelQuery) {
        setTimeout(() => {
          const actionMessage = {
            text: isRTL ? '<hotels-button>' : '<hotels-button>',
            sender: 'bot' as const,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, actionMessage]);
        }, 500);
      } else if (isTransportQuery) {
        setTimeout(() => {
          const actionMessage = {
            text: isRTL ? '<transport-button>' : '<transport-button>',
            sender: 'bot' as const,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, actionMessage]);
        }, 500);
      }
    }, 500);
  };
  
  const handleQuickReply = (message: string) => {
    const newUserMessage = {
      text: message,
      sender: 'user' as const,
      timestamp: new Date()
    };
    
    setMessages([...messages, newUserMessage]);
    
    // Process quick replies
    setTimeout(() => {
      let botResponse = '';
      let isHotelQuery = false;
      let isBookingQuery = false;
      let isTransportQuery = false;
      
      if (message === (isRTL ? 'اود الحجز' : 'I want to book')) {
        botResponse = getRandomResponse('booking');
        isBookingQuery = true;
      } else if (message === (isRTL ? 'كم سعر الليلة؟' : 'How much per night?')) {
        botResponse = getRandomResponse('booking');
        isBookingQuery = true;
      } else if (message === (isRTL ? 'ما هي الفنادق المتاحة؟' : 'What hotels are available?')) {
        botResponse = getRandomResponse('hotels');
        isHotelQuery = true;
      } else if (message === (isRTL ? 'خدمات النقل' : 'Transportation services')) {
        botResponse = getRandomResponse('transportation');
        isTransportQuery = true;
      } else if (message === (isRTL ? 'معلومات عن العمرة' : 'Umrah information')) {
        botResponse = getRandomResponse('umrah');
      }
      
      const newBotMessage = {
        text: botResponse,
        sender: 'bot' as const,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, newBotMessage]);
      
      // Add action buttons after response
      if (isBookingQuery) {
        setTimeout(() => {
          const actionMessage = {
            text: isRTL ? '<whatsapp-button>' : '<whatsapp-button>',
            sender: 'bot' as const,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, actionMessage]);
        }, 500);
      } else if (isHotelQuery) {
        setTimeout(() => {
          const actionMessage = {
            text: isRTL ? '<hotels-button>' : '<hotels-button>',
            sender: 'bot' as const,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, actionMessage]);
        }, 500);
      } else if (isTransportQuery) {
        setTimeout(() => {
          const actionMessage = {
            text: isRTL ? '<transport-button>' : '<transport-button>',
            sender: 'bot' as const,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, actionMessage]);
        }, 500);
      }
    }, 500);
  };
  
  return (
    <footer className="bg-black text-white">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Social Media Links */}
        <div className="flex justify-center mb-8">
          <div className="flex space-x-4 rtl:space-x-reverse">
            <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center cursor-default">
              {renderIcon(IoLogoTiktok, 18)}
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center cursor-default">
              {renderIcon(IoLogoSnapchat, 18)}
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center cursor-default">
              {renderIcon(IoLogoTwitter, 18)}
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center cursor-default">
              {renderIcon(IoLogoYoutube, 18)}
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center cursor-default">
              {renderIcon(IoLogoInstagram, 18)}
            </div>
            <a 
              href="https://www.facebook.com/share/1HFhYuvKWe/?mibextid=wwXIfr" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => trackOutboundLink('https://www.facebook.com/share/1HFhYuvKWe/?mibextid=wwXIfr', 'Facebook')}
              className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
            >
              {renderIcon(IoLogoFacebook, 18)}
            </a>
            <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center cursor-default">
              {renderIcon(IoLogoLinkedin, 18)}
            </div>
          </div>
        </div>
        
        {/* Contact Information */}
        <div className="text-center mb-8">
          <h3 className="text-lg font-semibold mb-4 text-emerald-500">{isRTL ? 'تواصل معنا' : 'Contact Us'}</h3>
          <p className="text-white mb-2">+966 56 682 7460</p>
          <h3 className="text-lg font-semibold mb-2 mt-4 text-emerald-500">{isRTL ? 'البريد الإلكتروني' : 'Email'}</h3>
          <p className="text-white">support@adwaaelmajd.com</p>
        </div>
        
        {/* Footer Links */}
        <div className="flex justify-center flex-wrap gap-4 text-sm text-gray-400 mb-4">
          <Link to="/" className="hover:text-white">{isRTL ? 'الرئيسية' : 'Home'}</Link>
          <Link to="/hotels" className="hover:text-white">{isRTL ? 'الفنادق' : 'Hotels'}</Link>
          <Link to="/buses" className="hover:text-white">{isRTL ? 'وسائل النقل' : 'Transportation'}</Link>
          <Link to="/blog" className="hover:text-white">{isRTL ? 'المدونة' : 'Blog'}</Link>
          <Link to="/contact" className="hover:text-white">{isRTL ? 'تواصل معنا' : 'Contact'}</Link>
          <Link to="/terms" className="hover:text-white">{isRTL ? 'الشروط والأحكام' : 'Terms & Conditions'}</Link>
          <Link to="/privacy" className="hover:text-white">{isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}</Link>
          <button onClick={() => changeLanguage(language === 'ar' ? 'en' : 'ar')} className="hover:text-white">
            {isRTL ? 'English' : 'عربية'}
          </button>
        </div>
        
        {/* Copyright */}
        <div className="text-center text-sm text-gray-500">
          <p>Copyright 2025 اضواء الحجاز لتنظيم رحلات العمرة</p>
        </div>
        
        {/* Back to Top Button */}
        <div className="flex justify-center mt-4">
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="rounded-full bg-gray-800 p-3 hover:bg-gray-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* WhatsApp Fixed Button */}
      <a 
        href={`https://wa.me/966566827460?text=${encodeURIComponent('انا اتيك من موقع اضواء الحجاز وارد الاستفسار عن رحلات العمره')}`} 
        target="_blank" 
        rel="noopener noreferrer"
        onClick={() => trackEvent('whatsapp_fixed_button_click', { source: 'floating_button' })}
        className={`fixed ${isRTL ? 'left-6' : 'right-6'} bottom-20 z-50 bg-emerald-500 text-white p-3 rounded-full shadow-lg hover:bg-emerald-600 transition-all duration-300`}
      >
        {renderIcon(IoLogoWhatsapp, 24)}
      </a>

      {/* Chatbot Fixed Button */}
      <button 
        onClick={() => setIsChatOpen(true)}
        className={`fixed ${isRTL ? 'left-6' : 'right-6'} bottom-6 z-50 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-900 transition-all duration-300`}
      >
        {renderIcon(IoChatbubbleEllipsesOutline, 24)}
      </button>
      
      {/* Chatbot Interface */}
      {isChatOpen && (
        <div className={`fixed ${isRTL ? 'left-6 sm:left-8' : 'right-6 sm:right-8'} bottom-24 z-50 bg-white rounded-lg shadow-xl w-[90vw] max-w-[350px] sm:max-w-[400px] max-h-[80vh] flex flex-col`}>
          {/* Chat Header */}
          <div className="bg-emerald-700 text-white p-3 sm:p-4 rounded-t-lg flex justify-between items-center">
            <h3 className="font-bold text-sm sm:text-base">{isRTL ? 'المساعد الآلي - اضواء الحجاز' : 'Adwaa Al-Hijaz Chatbot'}</h3>
            <button 
              onClick={() => setIsChatOpen(false)}
              className="text-white hover:text-gray-200"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          {/* Chat Messages */}
          <div className="flex-1 p-3 sm:p-4 overflow-y-auto max-h-[50vh] sm:max-h-[60vh] min-h-[200px] sm:min-h-[300px]" style={{direction: isRTL ? 'rtl' : 'ltr'}}>
            {messages.length === 0 ? (
              <div className="text-center text-gray-500 my-4">
                <p>{isRTL ? 'مرحباً بك في اضواء الحجاز للعمرة! كيف يمكنني مساعدتك اليوم؟' : 'Welcome to Adwaa Al-Hijaz Umrah! How can I help you today?'}</p>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div key={index} className={`mb-3 ${msg.sender === 'user' ? 'text-right' : 'text-left'}`}>
                  <div 
                    className={`inline-block rounded-lg px-3 py-2 max-w-[85%] ${
                      msg.sender === 'user' 
                        ? 'bg-emerald-100 text-emerald-900' 
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    {msg.text === '<whatsapp-button>' ? (
                      <a 
                        href={`https://wa.me/966566827460?text=${encodeURIComponent('انا اتيك من موقع اضواء الحجاز وارد الاستفسار عن رحلات العمره')}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block bg-emerald-500 text-white px-3 py-2 rounded-md hover:bg-emerald-600 transition-colors text-sm sm:text-base"
                      >
                        {isRTL ? 'تواصل عبر الواتساب' : 'Contact via WhatsApp'}
                      </a>
                    ) : msg.text === '<hotels-button>' ? (
                      <Link 
                        to="/hotels"
                        className="inline-block bg-emerald-700 text-white px-3 py-2 rounded-md hover:bg-emerald-800 transition-colors text-sm sm:text-base"
                        onClick={() => setIsChatOpen(false)}
                      >
                        {isRTL ? 'عرض الفنادق' : 'View Hotels'}
                      </Link>
                    ) : msg.text === '<transport-button>' ? (
                      <Link 
                        to="/buses"
                        className="inline-block bg-emerald-700 text-white px-3 py-2 rounded-md hover:bg-emerald-800 transition-colors text-sm sm:text-base"
                        onClick={() => setIsChatOpen(false)}
                      >
                        {isRTL ? 'خدمات النقل' : 'Transportation Services'}
                      </Link>
                    ) : (
                      <span className="text-sm sm:text-base">{msg.text}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Quick Replies */}
          <div className="p-2 border-t border-gray-200">
            <div className="flex flex-wrap gap-2 mb-2">
              <button 
                onClick={() => handleQuickReply(isRTL ? 'اود الحجز' : 'I want to book')}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs sm:text-sm px-2 sm:px-3 py-1 rounded-full transition-colors"
              >
                {isRTL ? 'اود الحجز' : 'I want to book'}
              </button>
              <button 
                onClick={() => handleQuickReply(isRTL ? 'كم سعر الليلة؟' : 'How much per night?')}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs sm:text-sm px-2 sm:px-3 py-1 rounded-full transition-colors"
              >
                {isRTL ? 'كم سعر الليلة؟' : 'How much per night?'}
              </button>
              <button 
                onClick={() => handleQuickReply(isRTL ? 'ما هي الفنادق المتاحة؟' : 'What hotels are available?')}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs sm:text-sm px-2 sm:px-3 py-1 rounded-full transition-colors"
              >
                {isRTL ? 'ما هي الفنادق المتاحة؟' : 'What hotels are available?'}
              </button>
              <button 
                onClick={() => handleQuickReply(isRTL ? 'خدمات النقل' : 'Transportation services')}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs sm:text-sm px-2 sm:px-3 py-1 rounded-full transition-colors"
              >
                {isRTL ? 'خدمات النقل' : 'Transportation services'}
              </button>
              <button 
                onClick={() => handleQuickReply(isRTL ? 'معلومات عن العمرة' : 'Umrah information')}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs sm:text-sm px-2 sm:px-3 py-1 rounded-full transition-colors"
              >
                {isRTL ? 'معلومات عن العمرة' : 'Umrah information'}
              </button>
            </div>
          </div>
          
          {/* Chat Input */}
          <div className="p-2 sm:p-3 border-t border-gray-200">
            <div className="flex">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder={isRTL ? 'اكتب رسالتك هنا...' : 'Type your message here...'}
                className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-black"
                style={{direction: isRTL ? 'rtl' : 'ltr'}}
              />
              <button
                onClick={handleSendMessage}
                className="bg-emerald-700 text-white px-3 py-2 rounded-r-lg hover:bg-emerald-800 transition-colors"
              >
                {isRTL ? 'إرسال' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
